import { computed } from "vue";
import { paletteFor, spreadShades } from "./useChartUtils";
import { resolveLocalizedTitle } from "./useCardData";

/**
 * Pie/donut chart data composable.
 */
export function usePieChartData(ctx) {
  const {
    displayLocale,
    rawData,
    codeLabels,
    includeSkips,
    choiceColors,
    singlePopulation,
    getAdjustedBase,
    getSkippedCount,
    getAnswerCount,
    getAnswerPercentage,
    getAnswerDisplayOrder,
  } = ctx;

  // The pie renders one displayed population (see singlePopulation in useCardData);
  // a population whose slice is not loaded falls back to the full base.
  const pieSource = computed(() => {
    const pop = singlePopulation.value;
    const popRaw = pop?.key ? rawData.value?.[pop.key] : null;
    return popRaw?.answers ? popRaw : rawData.value;
  });

  const pieData = computed(() => {
    if (!pieSource.value?.answers) return [];
    const entries = Object.entries(pieSource.value.answers)
      .filter(([, a]) => getAnswerCount(a) > 0)
      .sort(([, a], [, b]) => getAnswerDisplayOrder(a) - getAnswerDisplayOrder(b));
    const base = includeSkips.value ? getAdjustedBase(pieSource.value) : null;
    const skippedNb = includeSkips.value ? getSkippedCount(pieSource.value) : 0;
    const totalEntries = skippedNb > 0 ? entries.length + 1 : entries.length;
    const popColor = singlePopulation.value?.color;
    const palette = paletteFor(popColor);
    const colors = spreadShades(palette, totalEntries);
    const cc = choiceColors.value;
    const result = entries.map(([code, answer], index) => {
      const nb = getAnswerCount(answer);
      return {
        code,
        label: codeLabels.value[code] || resolveLocalizedTitle(answer.title, displayLocale.value) || code,
        value: nb,
        percentage: base != null && base > 0 ? nb / base : getAnswerPercentage(answer),
        fill: cc?.[code] || colors[index],
      };
    });
    if (skippedNb > 0) {
      result.push({
        code: "999",
        label: "Skip",
        value: skippedNb,
        percentage: base > 0 ? skippedNb / base : 0,
        fill: colors[totalEntries - 1],
      });
    }
    return result;
  });

  return { pieData };
}
